import { z } from 'zod';

// Proposal lifecycle.
//   draft     — staff is building it; not visible to client
//   sent      — emailed to client; awaiting decision
//   accepted  — client signed (advances the parent request → engagement)
//   declined  — client said no (with optional reason)
//   withdrawn — staff retracted while in 'sent'
//   expired   — past valid_until without acceptance
export const PROPOSAL_STATUSES = ['draft', 'sent', 'accepted', 'declined', 'withdrawn', 'expired'] as const;
export const ProposalStatusSchema = z.enum(PROPOSAL_STATUSES);
export type ProposalStatus = z.infer<typeof ProposalStatusSchema>;

// Payment plans Omega offers. Free-form notes live elsewhere; these are the
// four canonical structures shown on /services and in the legal pages.
//   50_50       — 50% on engagement, 50% on delivery
//   30_40_30    — 30% engagement, 40% milestone, 30% delivery
//   milestones  — staff-defined milestone array
//   success_fee — primarily contingent on outcome (rare; FCA-sensitive — needs
//                 compliance review per REQ-013)
export const PROPOSAL_PAYMENT_PLANS = ['50_50', '30_40_30', 'milestones', 'success_fee'] as const;
export const ProposalPaymentPlanSchema = z.enum(PROPOSAL_PAYMENT_PLANS);
export type ProposalPaymentPlan = z.infer<typeof ProposalPaymentPlanSchema>;

export const ISO_CURRENCY = ['GBP', 'USD', 'EUR', 'AED', 'INR', 'SGD'] as const;
export const ProposalCurrencySchema = z.enum(ISO_CURRENCY);
export type ProposalCurrency = z.infer<typeof ProposalCurrencySchema>;

// A single milestone row used when payment_plan === 'milestones'.
export const ProposalMilestoneSchema = z.object({
  label: z.string().min(1).max(200),
  amount_pence: z.number().int().nonnegative(),
  due_at: z.string().datetime().nullable().optional(),
});
export type ProposalMilestone = z.infer<typeof ProposalMilestoneSchema>;

export const ProposalDeliverableSchema = z.object({
  label: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
});
export type ProposalDeliverable = z.infer<typeof ProposalDeliverableSchema>;

// Input shape for POST /api/proposals (create draft). request_id is required
// — proposals never exist independently of a brief.
export const ProposalInputSchema = z.object({
  request_id: z.string().min(1),
  scope_markdown: z.string().min(1).max(20_000),
  deliverables: z.array(ProposalDeliverableSchema).max(20).default([]),
  assumptions: z.string().max(5000).optional(),

  fee_pence: z.number().int().nonnegative(),
  currency: ProposalCurrencySchema,

  payment_plan: ProposalPaymentPlanSchema,
  milestones: z.array(ProposalMilestoneSchema).max(20).optional(),
  success_fee_terms: z.string().max(2000).optional(),

  valid_until: z.string().datetime(),
});
export type ProposalInput = z.infer<typeof ProposalInputSchema>;

// Patch shape (staff edits during draft state). All fields optional.
export const ProposalPatchSchema = ProposalInputSchema
  .omit({ request_id: true })
  .partial();
export type ProposalPatch = z.infer<typeof ProposalPatchSchema>;

// Public DTO returned by GET endpoints.
export const ProposalDtoSchema = z.object({
  id: z.string(),
  request_id: z.string(),
  client_id: z.string(),
  client_name: z.string().optional(),

  status: ProposalStatusSchema,

  scope_markdown: z.string(),
  deliverables: z.array(ProposalDeliverableSchema),
  assumptions: z.string().nullable().optional(),

  fee_pence: z.number().int().nonnegative(),
  currency: ProposalCurrencySchema,

  payment_plan: ProposalPaymentPlanSchema,
  milestones: z.array(ProposalMilestoneSchema),
  success_fee_terms: z.string().nullable().optional(),

  valid_until: z.string(),
  disclaimers_version: z.string().nullable().optional(),

  sent_at: z.string().nullable().optional(),
  accepted_at: z.string().nullable().optional(),
  declined_at: z.string().nullable().optional(),
  decline_reason: z.string().nullable().optional(),

  created_by_staff_id: z.string(),
  accepted_by_user_id: z.string().nullable().optional(),

  created_at: z.string(),
  updated_at: z.string(),
});
export type ProposalDto = z.infer<typeof ProposalDtoSchema>;
